import { useRef, useState } from "react";
import { Button } from "@deere/fuel-react";
import ExcelJS from "exceljs/dist/exceljs.min";
import { getExention, convertToJson } from "../../../utils/fileUtil";
import { ERR_FILE_TYPE, ERR_INVALID_FILE } from "../../../utils/errorLiterals";
import ModalWarningInactiveParts from "./ModalWarningInactiveParts";
import { POPUP_QUESTIONS_TITLES, QUESTION_TYPES } from "../../../utils/constants";
import { useTranslation } from "react-i18next";
import { getBoundary } from "../../../services/external.service";
import { useDispatch, useSelector } from 'react-redux';
import { updateStateByQuesIdList } from "../../../redux/action/surveyPartAction";

const UploadPartsTable = ({ data, fixedColumns, questions, rowsToDisable, setData, updatePartsArray, setTransactionParts, transactionParts, setPartErrorIds, sampleData, isAuthMethodOkta, token, setExcelUploading, showWarningModal, setShowWarningModal, activeDataForUpload, inactiveDataForWarning, setActiveDataForUpload, setInactiveDataForWarning }) => {
  const { t } = useTranslation();
  const hiddenFileInput = useRef(null);
  const partNumberKey = "deerePartNumber";
  const activeData = data.filter((d) => !rowsToDisable.includes(d.id));
  const dispatch = useDispatch();
  const countryList = useSelector(state => state.surveyPartReducer.countryList);
  const stateList = useSelector(state => state.surveyPartReducer.stateList);

  const columns = [];
  const countryQueIds = [];
  const stateQueIds = [];
  const checkQueIds = [];
  fixedColumns.forEach((col) => columns.push({id: col.field, headerName: col.headerName}));
  questions.forEach((que) => {
    if (!POPUP_QUESTIONS_TITLES.includes(que.type)) {
      if (!que.idQuestionDepends || que.idQuestionDepends === 0) {
        columns.push({id: `que_${que.id}`, headerName: que.text});
      } else {
        let parent = questions.find((q) => q.id === que.idQuestionDepends);
        if (parent && parent.type === QUESTION_TYPES.CHEK_POPUP && que.type === QUESTION_TYPES.SELECT) {
          columns.push({id: `que_${que.id}`, headerName: `${parent.text} - ${que.text}`});
        } else {
          columns.push({id: `que_${que.id}`, headerName: que.text});
        }
      }
    }
    if ((que.type === QUESTION_TYPES.SEL_CNTRY || que.type === QUESTION_TYPES.SEL_CTY_UW)) countryQueIds.push(que.id);
    if (que.type === QUESTION_TYPES.SEL_STATE) stateQueIds.push({ id: que.id, parentId: que.idQuestionDepends });
    if (que.type === QUESTION_TYPES.CHECK) checkQueIds.push(que.id);
  });

  const triggerFileUpload = () => {
    hiddenFileInput.current.value = "";
    hiddenFileInput.current.click();
  };

  const checkForInactiveData = (data) => {
    const dataInactive = [];
    const dataActive = [];
    data.forEach((d) => {
      let index = activeData.findIndex((ad) => ad[partNumberKey] === d[partNumberKey]);
      if (index > -1) dataActive.push(d);
      else dataInactive.push(d);
    });
    setActiveDataForUpload(dataActive);
    setInactiveDataForWarning(dataInactive);
    if (dataInactive.length > 0){
      setShowWarningModal(true);
      setExcelUploading(true);
    }
    else fetchStatesForCountries(dataActive);
  };

  const fetchStatesForCountries = async (data) => {
    const countryCodes = [];
    const dataToSubmit = data && Array.isArray(data) ? [...data] : [...activeDataForUpload];
    dataToSubmit.forEach((d) => {
      countryQueIds.forEach((id) => {
        const countryIndex = countryList.findIndex((c) => c.name === d[`que_${id}`]);
        if (countryIndex > -1) {
          const index1 = stateList.findIndex((s) => s.country === countryList[countryIndex].name);
          const index2 = countryCodes.findIndex((c) => c === countryList[countryIndex].iso2CountryCode);
          if (index1 === -1 && index2 === -1) countryCodes.push(countryList[countryIndex].iso2CountryCode);
        } else {
          d[`que_${id}`] = "";
        }
      });
    });
    if (countryCodes.length > 0) {
      const result = await Promise.all(countryCodes.map((code) => getBoundary(token, code)));
      const mappedResult = countryCodes.map((code, index) => {
        const country = countryList.find((c) => c.iso2CountryCode === code);
        return { country: country.name, states: result[index] };
      });
      dispatch(updateStateByQuesIdList(mappedResult));
      dataToSubmit.forEach((d) => {
        stateQueIds.forEach((s) => {
          if (d[`que_${s.parentId}`] !== "") {
            const idx1 = stateList.findIndex((s) => s.country === d[`que_${s.parentId}`]);
            const idx2 = idx1 === -1 ? mappedResult.findIndex((r) => r.country === d[`que_${s.parentId}`]) : -1;
            const arrStates = idx1 > -1 ? stateList[idx1].states : idx2 > -1 ? mappedResult[idx2].states : [];
            const stateIndex = arrStates.findIndex((st) => st.name === d[`que_${s.id}`]);
            if (stateIndex === -1) d[`que_${s.id}`] = "";
          } else {
            d[`que_${s.id}`] = "";
          }
        });
      });
      addDataForColumnHeader(dataToSubmit);
    } else {
      addDataForColumnHeader(dataToSubmit);
    }
  };

  const addDataForColumnHeader = (dataToSubmit) => {
    const columnHeaderQues = questions.filter((que) => POPUP_QUESTIONS_TITLES.includes(que.type));
    columnHeaderQues.forEach((header) => {
      const directChild = questions.find((que) => que.idQuestionDepends === header.id);
      if (directChild) {
        dataToSubmit.forEach((data) => {
          if (header.type === QUESTION_TYPES.TITL_POPUP) {
            data["que_" + header.id] = data["que_" + directChild.id];
          } else if (header.type === QUESTION_TYPES.CHEK_POPUP && data["que_" + directChild.id] && data["que_" + directChild.id] !== "") {
            data["que_" + header.id] = "1";
          }
        });
      }
    });
    submitUploadedData(dataToSubmit);
  };

  const getSelectionValues = (data) => {
    for (const key in data) {
      if (key.split("_")[0] === "que") {
        let foundQue = questions.find((que) => que.id.toString() === key.split("_")[1]);
        if (foundQue && foundQue.selections.length > 0) {
          const selectedAns = foundQue.selections.find((sel) => sel.text === data[key]);
          if (selectedAns) data[key] = selectedAns.value;
        }
      }
    }
    return data;
  };

  const submitUploadedData = (dataToUpload) => {
    let newData = [...data];
    dataToUpload.forEach((dtu) => {
      const data = getSelectionValues(dtu);
      let index = newData.findIndex((item) => item[partNumberKey] === data[partNumberKey]);
      if (index > -1) {
        data.id = newData[index].id;
        newData[index] = { ...newData[index], ...data };

        //Convert check question , if true then store true, else empty
        for (const queId of checkQueIds) {
          let  questionCheck=dtu[`que_${queId}`];
          if (questionCheck) {
            questionCheck = questionCheck.toLowerCase();
            if (questionCheck !== "true"){
              dtu[`que_${queId}`]="";
              newData[index][`que_${queId}`]="";
            }else {
              dtu[`que_${queId}`]=questionCheck;
              newData[index][`que_${queId}`]=questionCheck;
            }
          }  
        }
      }
    });
    setData([...newData]);
    updateTxnData(dataToUpload);
    setExcelUploading(false);
    setShowWarningModal(false);
  };

  const updateTxnData = (dataToUpload) => {
    let tempArr = [...transactionParts];
    dataToUpload.forEach((data) => {
      for (const key in data) {
        if (key.split("_")[0] === "que") {
          let index = tempArr.findIndex((p) => p.partId === data.id && "que_" + p.questionId === key);
          if (index > -1 && data[key] !== "") {
            tempArr[index].answer = data[key];
          } else if (index === -1 && data[key] !== "") {
            tempArr.push({ partId: data.id, questionId: key.split("_")[1], answer: data[key] });
          } else if (index > -1 && data[key] === "") {
            tempArr.splice(index, 1);
          }
        }
      }
    });
    updatePartsArray(tempArr);
    setTransactionParts(tempArr);
    setPartErrorIds([]);
  };

  const formatTheFileData = (fileData) => {
    fileData = fileData.map((row) => row.map((cell) => cell.toString()));
    // Iterate backwards because we are removing elements from each row (fd.splice(i+1, 1)).
    // Reverse iteration prevents index shifting issues that would occur if we iterated forwards.
    for(let i=columns.length-1; i>=0; i--) {
      const queIndex = questions.findIndex((que) => `que_${que.id}` === columns[i].id);
      if (queIndex > -1 && questions[queIndex].type === QUESTION_TYPES.MULT_CNTRY) {
        fileData.forEach((fd, fdIndex) => {
          if (fdIndex > 0)  fd[i] = (typeof fd[i+1] !== "undefined" && fd[i+1] !== "") ? `${fd[i]}, ${fd[i+1]}` : fd[i];
          fd.splice(i+1, 1);
        });
      }
    }
    return fileData;
  };

  const handleFileChange = (event) => {
    const fileObj = event.target.files && event.target.files[0];
    if (!fileObj) {
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      setExcelUploading(true);
      const workBook = new ExcelJS.Workbook()
      await workBook.xlsx.load(event.target.result)
      const workSheet = workBook.worksheets[0]
      let fileData = []
      workSheet.eachRow({ includeEmpty: false }, (row) => {
        fileData.push(row.values.slice(1).map(cell => (cell === null || cell === undefined) ? "" : cell))
      })
      fileData = formatTheFileData(fileData);
      const headers = fileData[0];
      const colHeaderNames = columns.map((col) => col.headerName);
      const colIds = columns.map((col) => col.id);
      if (headers.toString() === colHeaderNames.toString()) {
        let jsonData = convertToJson(colIds, fileData);
        if(sampleData.length > 0){
          jsonData.shift();
        }
        checkForInactiveData(jsonData);
      } else {
        alert(ERR_INVALID_FILE);
        setExcelUploading(false);
      }
      event.target.value = "";
    };
    if (fileObj) {
      if (getExention(fileObj)) {
        reader.readAsArrayBuffer(fileObj);
      } else {
        alert(ERR_FILE_TYPE);
      }
    }
  };

  return (
    <>
      <input style={{ display: "none" }} type="file" ref={hiddenFileInput} onChange={handleFileChange} />
      <Button onClick={triggerFileUpload}>{t("btn_upload_tbl_data")}</Button>
      <ModalWarningInactiveParts showModal={showWarningModal} setShowModal={setShowWarningModal} inactiveParts={inactiveDataForWarning} submitHandler={fetchStatesForCountries} setExcelUploading={setExcelUploading} />
    </>
  );
};

export default UploadPartsTable;
